import React, { useState, useMemo, useEffect } from 'react';
import { EXAM_DATES, SUBJECT_PALETTE } from '../constants';
import './SetupView.css';

const DAYS_OF_WEEK_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAYS_OF_WEEK_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MBE_SUBJECTS = ['torts', 'contracts', 'property', 'civpro', 'evidence', 'criminal', 'constitution'];
 
function SetupView({ onSetupComplete, subjects, userData }) {
  const [step, setStep] = useState(1);
  
  // --- STATE ---
  // Step 1: User Info
  const [name, setName] = useState(userData?.name || userData?.session?.user?.user_metadata?.full_name || ''); 
  const [phone, setPhone] = useState(userData?.profile?.phone || '');

  // Step 2: Exam
  const [examDate, setExamDate] = useState(EXAM_DATES[0].value); 

  // Step 3: Context
  const [isRetaker, setIsRetaker] = useState(null); 
  const [isSupplement, setIsSupplement] = useState(null);
  const [prepCourseName, setPrepCourseName] = useState('');

  // Step 4: Nightmare Sort
  const [nightmareSubjects, setNightmareSubjects] = useState([]);

  // Step 5: Strategy
  const [archetype, setArchetype] = useState(null); 
  const [isCustomizing, setIsCustomizing] = useState(false);
  
  // Detailed Config
  const [mixMode, setMixMode] = useState(false);
  const [subjectSequence, setSubjectSequence] = useState(subjects.map(s => s.subject_id));
  const [hfrRestrictions, setHfrRestrictions] = useState(new Set());
  const [denovoTrackSubjects, setDenovoTrackSubjects] = useState(new Set());
  const [excludedSubjects, setExcludedSubjects] = useState(new Set());
  
  // Step 6: Availability
  const [studyDays, setStudyDays] = useState(userData?.goals?.study_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);

  // Internal Drag State
  const [draggedItem, setDraggedItem] = useState(null);

  const subjectMap = useMemo(() => subjects.reduce((acc, s) => ({ ...acc, [s.subject_id]: s.name }), {}), [subjects]);

  const weeksUntilExam = useMemo(() => {
      if (!examDate) return 99;
      const now = new Date();
      const exam = new Date(examDate + 'T00:00:00');
      const diffTime = exam - now;
      if (diffTime < 0) return 0;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
  }, [examDate]);

  const renderTimeWarning = () => {
      if (weeksUntilExam > 0 && weeksUntilExam < 8) {
          return (
              <div className="time-warning-banner fade-in">
                  <span className="material-symbols-outlined">warning</span>
                  <div>
                      <strong>Crunch Time:</strong> With only {weeksUntilExam} weeks left, consider enabling <strong>High-Yield (HFR)</strong> or <strong>Fast Track</strong> for at least 8 subjects to ensure full coverage.
                  </div>
              </div>
          );
      }
      return null;
  };

  // --- LOGIC ---

  // Apply Nightmare Sort when entering Step 5
  useEffect(() => {
      if (step === 5 && archetype === null && !isCustomizing) {
          const baseIds = subjects.map(s => s.subject_id).filter(id => !nightmareSubjects.includes(id));
          setSubjectSequence([...nightmareSubjects, ...baseIds]);
      }
  }, [step, nightmareSubjects, subjects, archetype, isCustomizing]);

  const selectArchetype = (type) => {
      setArchetype(type);
      
      const allSubjectIds = subjects.map(s => s.subject_id);
      const nonMbeIds = allSubjectIds.filter(id => !MBE_SUBJECTS.includes(id));

      setDenovoTrackSubjects(new Set());
      setExcludedSubjects(new Set());

      switch (type) {
          case 'traditional':
              setMixMode(false);
              setHfrRestrictions(new Set());
              break;
          case 'interleaved':
              setMixMode(true);
              setHfrRestrictions(new Set());
              break;
          case 'high_yield':
              setMixMode(true);
              setHfrRestrictions(new Set(allSubjectIds));
              break;
          case 'shield':
              setMixMode(false);
              setHfrRestrictions(new Set(nonMbeIds));
              break;
          default:
              break;
      }
  };

  const toggleSet = (setter, set, value) => {
      const newSet = new Set(set);
      if (newSet.has(value)) newSet.delete(value);
      else newSet.add(value);
      setter(newSet);
  };

  const toggleDay = (day) => {
    const dayLower = day.toLowerCase();
    if (studyDays.includes(dayLower)) setStudyDays(studyDays.filter(d => d !== dayLower));
    else setStudyDays([...studyDays, dayLower]);
  };

  const toggleNightmare = (subjectId) => {
      if (nightmareSubjects.includes(subjectId)) {
          setNightmareSubjects(nightmareSubjects.filter(id => id !== subjectId));
      } else {
          if (nightmareSubjects.length < 3) {
              setNightmareSubjects([...nightmareSubjects, subjectId]);
          }
      }
  };

  const handleDragStart = (e, index) => { 
      if (!isCustomizing) return;
      setDraggedItem(index); 
      e.dataTransfer.effectAllowed = "move"; 
  };
  
  const handleDragOver = (e, index) => {
      e.preventDefault();
      if (!isCustomizing || draggedItem === null || draggedItem === index) return;
      const newSequence = [...subjectSequence];
      const item = newSequence.splice(draggedItem, 1)[0];
      newSequence.splice(index, 0, item);
      setDraggedItem(index);
      setSubjectSequence(newSequence);
  };

  const handleFinalize = () => {
      const targetDateObj = new Date(examDate + 'T12:00:00');
      targetDateObj.setDate(targetDateObj.getDate() - 7);
      
      const year = targetDateObj.getFullYear();
      const month = String(targetDateObj.getMonth() + 1).padStart(2, '0');
      const day = String(targetDateObj.getDate()).padStart(2, '0');
      const targetDateStr = `${year}-${month}-${day}`;

      const updates = {
          profile: {
              name: name,
              phone: phone
          },
          goals: {
              ...(userData?.goals || {}),
              bar_exam_date: examDate,
              target_mastery_date: targetDateStr,
              study_days: studyDays,
              mix_mode: mixMode,
              hfr_restricted_subjects: Array.from(hfrRestrictions),
              denovo_track_subjects: Array.from(denovoTrackSubjects),
              excluded_subjects: Array.from(excludedSubjects)
          },
          diagnostics: {
              ...(userData?.diagnostics || {}),
              subject_sequence: subjectSequence,
              prep_course: {
                  is_enrolled: isSupplement,
                  name: isSupplement ? (prepCourseName || 'Commercial Course') : null
              },
              is_retaker: isRetaker
          }
      };
      onSetupComplete(updates);
  };


  // --- RENDERERS ---

  const renderStep1_Profile = () => (
    <div className="setup-step fade-in">
        <h2 className="step-title">Welcome to Wali's!</h2>
        <p className="step-subtitle">Let's get your account set up.</p>
        <div className="profile-form-group">
            <div className="form-field">
                <label htmlFor="full-name">Full Name</label>
                <input
                    id="full-name"
                    type="text"
                    className="text-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Jane Doe"
                    required
                />
            </div>
            <div className="form-field">
                <label htmlFor="phone">Phone Number</label>
                <input
                    id="phone"
                    type="tel"
                    className="text-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                />
            </div>
        </div>
    </div>
  );

  const renderStep2_Exam = () => (
      <div className="setup-step fade-in">
          <h2 className="step-title">Which exam are you taking?</h2>
          <div className="exam-selector-container">
              {EXAM_DATES.map((dateOpt) => (
                  <button 
                      key={dateOpt.value}
                      className={`exam-option-btn ${examDate === dateOpt.value ? 'active' : ''}`}
                      onClick={() => setExamDate(dateOpt.value)}
                  >
                      {dateOpt.label}
                  </button>
              ))}
          </div>
      </div>
  );

  const renderStep3_Context = () => (
      <div className="setup-step fade-in">
          <h2 className="step-title">Let's get some context.</h2>
          
          <div className="context-question">
              <label>Is this your first time taking the Bar Exam?</label>
              <div className="pill-selector">
                  <button className={isRetaker === false ? 'active' : ''} onClick={() => setIsRetaker(false)}>First Timer</button>
                  <button className={isRetaker === true ? 'active' : ''} onClick={() => setIsRetaker(true)}>Returning Taker</button>
              </div>
          </div>

          <div className="context-question">
              <label>Are you using Wali's as a supplement to a course?</label>
              <div className="pill-selector">
                  <button className={isSupplement === false ? 'active' : ''} onClick={() => setIsSupplement(false)}>No, Primary Tool</button>
                  <button className={isSupplement === true ? 'active' : ''} onClick={() => setIsSupplement(true)}>Yes, Supplement</button>
              </div>
          </div>

          {isSupplement && (
              <div className="supplement-input-container fade-in">
                  <label className="input-label">Which course?</label>
                  <input 
                      type="text" 
                      className="text-input"
                      placeholder="e.g. Barbri, Themis"
                      value={prepCourseName}
                      onChange={(e) => setPrepCourseName(e.target.value)}
                  />
                   <div className="tip-box">
                      <span className="material-symbols-outlined">lightbulb</span>
                      <p><strong>Pro Tip:</strong> Since you have another course, we recommend using <strong>High-Yield</strong> or <strong>The Shield</strong> strategy in step 4.</p>
                  </div>
              </div>
          )}
      </div>
  );

  const renderStep4_Nightmare = () => (
      <div className="setup-step fade-in">
          <h2 className="step-title">Which subjects keep you up at night?</h2>
          <p className="step-subtitle">Select up to 3. We will prioritize these in your schedule to tackle your anxiety head-on.</p>
          
          <div className="nightmare-grid">
              {subjects.map((s, idx) => {
                  const isSelected = nightmareSubjects.includes(s.subject_id);
                  const color = SUBJECT_PALETTE[idx % SUBJECT_PALETTE.length];
                  return (
                      <button 
                          key={s.subject_id}
                          className={`nightmare-pill ${isSelected ? 'active' : ''}`}
                          style={{ '--pill-color': color }}
                          onClick={() => toggleNightmare(s.subject_id)}
                      >
                          {s.name}
                      </button>
                  );
              })}
          </div>
      </div>
  );

  const renderStep5_Archetypes = () => (
    <div className="setup-step fade-in wide-content">
        <h2 className="step-title">Choose your Strategy Archetype</h2>
        {renderTimeWarning()}
        <div className="strategy-split-view">
            
            <div className="strategy-left">
                <div className="archetype-grid-2x2">
                    {[
                      { id: 'traditional', icon: 'menu_book', title: 'Traditional', tag: 'Linear Focus', desc: 'One subject at a time. Deep focus.' },
                      { id: 'interleaved', icon: 'shuffle', title: 'Interleaved', tag: 'Max Retention', desc: 'Mixed topics daily for better long-term memory.' },
                      { id: 'high_yield', icon: 'track_changes', title: 'High-Yield', tag: 'Efficiency', desc: 'Focus only on frequently tested rules (HFR).' },
                      { id: 'shield', icon: 'shield', title: 'The Shield', tag: 'Retaker Focus', desc: 'Attack weaknesses. HFR maintenance for the rest.' }
                    ].map(card => (
                        <button 
                          key={card.id}
                          className={`archetype-card ${archetype === card.id ? 'active' : ''}`} 
                          onClick={() => selectArchetype(card.id)}
                        >
                            <div className="arch-header">
                                <span className="material-symbols-outlined">{card.icon}</span>
                                <h3>{card.title}</h3>
                            </div>
                            <p className="tagline">{card.tag}</p>
                            <p className="desc">{card.desc}</p>
                        </button>
                    ))}
                </div>

                <div className="custom-section">
                    <div className="custom-toggle-row">
                        <span className="label">
                            <span className="material-symbols-outlined">tune</span> Manual Customization
                        </span>
                        <label className="toggle-switch">
                            <input type="checkbox" checked={isCustomizing} onChange={() => setIsCustomizing(!isCustomizing)} />
                            <span className="slider round"></span>
                        </label>
                    </div>
                    <p className="custom-hint">Enable this to manually drag subjects, exclude topics, or toggle specific strategy badges below.</p>
                </div>
            </div>

            <div className="strategy-right floating-panel">
                <div className="panel-header">
                    <h4>Curriculum Preview</h4>
                    <button 
                        className={`mix-badge-btn ${isCustomizing ? 'clickable' : ''}`}
                        onClick={() => isCustomizing && setMixMode(!mixMode)}
                        disabled={!isCustomizing}
                        title={isCustomizing ? "Click to toggle Mix Mode" : "Select an archetype to change"}
                    >
                        {mixMode ? (
                            <span className="badge mixed"><span className="material-symbols-outlined">shuffle</span> Mixed</span>
                        ) : (
                            <span className="badge linear"><span className="material-symbols-outlined">arrow_downward</span> Linear</span>
                        )}
                    </button>
                </div>

                <div className="panel-legend">
                    <div className="legend-item">
                        <span className="mini-badge hfr active static">HFR</span>
                        <div className="legend-text">
                            <strong>High Frequency Only</strong>
                            <span>Filters to only the most tested rules.</span>
                        </div>
                    </div>
                    <div className="legend-item">
                        <span className="mini-badge fast active static"><span className="material-symbols-outlined" style={{fontSize: '1em'}}>fast_forward</span></span>
                        <div className="legend-text">
                            <strong>Fast Track</strong>
                            <span>Skips typing drills. Goes straight to review.</span>
                        </div>
                    </div>
                </div>
                
                <div className="preview-list">
                    {subjectSequence.map((subjectId, index) => {
                        const isHFR = hfrRestrictions.has(subjectId);
                        const isFast = denovoTrackSubjects.has(subjectId);
                        const isExcluded = excludedSubjects.has(subjectId);
                        
                        const showHandle = isCustomizing && !mixMode;

                        return (
                          <div 
                              key={subjectId}
                              className={`preview-item ${showHandle ? 'draggable' : ''} ${isExcluded ? 'excluded' : ''}`}
                              draggable={showHandle && !isExcluded}
                              onDragStart={(e) => handleDragStart(e, index)}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDragEnd={() => setDraggedItem(null)}
                          >
                              <div className="preview-name-row">
                                  {showHandle && <span className="material-symbols-outlined handle">drag_indicator</span>}
                                  <span className="name">{subjectMap[subjectId]}</span>
                              </div>
                              
                              <div className="preview-badges">
                                  {isCustomizing && (
                                      <button 
                                          className="mini-icon-btn exclude"
                                          onClick={() => toggleSet(setExcludedSubjects, excludedSubjects, subjectId)}
                                          title={isExcluded ? "Include Subject" : "Exclude Subject"}
                                      >
                                          <span className="material-symbols-outlined">
                                              {isExcluded ? 'visibility' : 'visibility_off'}
                                          </span>
                                      </button>
                                  )}

                                  {!isExcluded && (
                                      <>
                                          <button 
                                              className={`mini-badge hfr ${isHFR ? 'active' : ''}`} 
                                              onClick={() => isCustomizing && toggleSet(setHfrRestrictions, hfrRestrictions, subjectId)}
                                              disabled={!isCustomizing}
                                              title="High Frequency Rules Only"
                                          >HFR</button>
                                          <button 
                                              className={`mini-badge fast ${isFast ? 'active' : ''}`}
                                              onClick={() => isCustomizing && toggleSet(setDenovoTrackSubjects, denovoTrackSubjects, subjectId)}
                                              disabled={!isCustomizing}
                                              title="Fast Track (De Novo Only)"
                                          ><span className="material-symbols-outlined" style={{fontSize: '1em'}}>fast_forward</span></button>
                                      </>
                                  )}
                              </div>
                          </div>
                        );
                    })}
                </div>
            </div>
        </div>
    </div>
);

  const renderStep6_Availability = () => (
      <div className="setup-step fade-in">
          <h2 className="step-title">When can you study?</h2>
          <div className="study-day-selector">
              {DAYS_OF_WEEK_KEYS.map((day, idx) => (
                  <button 
                      key={day} 
                      className={`study-day-btn week ${studyDays.includes(day) ? 'active' : ''}`} 
                      onClick={() => toggleDay(day)}
                  >
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                  </button>
              ))}
          </div>
      </div>
  );

  const renderStep7_Contract = () => {
      const targetDateObj = new Date(examDate + 'T12:00:00');
      targetDateObj.setDate(targetDateObj.getDate() - 7);
      const targetDateStr = targetDateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      
      const archetypeLabels = {
          traditional: 'Traditional Linear',
          interleaved: 'Interleaved Scientific',
          high_yield: 'High-Yield Sniper',
          shield: 'Shield Retaker'
      };

      const planName = archetype ? archetypeLabels[archetype] : 'Custom Strategy';

      return (
        <div className="setup-step fade-in contract-step">
            <h2 className="step-title">Commitment Protocol</h2>
            
            <div className="contract-card">
                <div className="contract-header">
                    <img src="/logo.png" alt="Wali" className="contract-avatar" />
                    <div className="contract-intro">
                        <h3>{name ? `${name}, welcome to Wali's.` : "Welcome to Wali's!"}</h3>
                    </div>
                </div>
                
                <div className="contract-body">
                    <p>We have built a <strong>{planName}</strong> plan for you.</p>
                    
                    {nightmareSubjects.length > 0 && (
                        <p>You are prioritizing <strong>{nightmareSubjects.map(id => subjectMap[id]).join(' and ')}</strong> to tackle your anxiety head-on.</p>
                    )}

                    {excludedSubjects.size > 0 && (
                         <p>You have excluded <strong>{excludedSubjects.size} subjects</strong> from your study schedule.</p>
                    )}

                    <p>Your target mastery date is <strong className="highlight-date">{targetDateStr}</strong>.</p>
                </div>

                <div className="contract-actions">
                    <button className="btn secondary contract-back-btn" onClick={() => setStep(5)}>
                        Adjust Strategy
                    </button>
                    <button className="btn contract-btn" onClick={handleFinalize}>
                        <span className="material-symbols-outlined">verified</span>
                        Accept Mission
                    </button>
                </div>
            </div>
        </div>
      );
  };

  const getStepContent = () => {
      switch(step) {
        case 1: return renderStep1_Profile();
        case 2: return renderStep2_Exam();
          case 3: return renderStep3_Context();
          case 4: return renderStep4_Nightmare();
          case 5: return renderStep5_Archetypes();
          case 6: return renderStep6_Availability();
          case 7: return renderStep7_Contract();
          default: return <div className="loading-spinner large"></div>;
      }
  };

  const canProceed = () => {
    const phoneIsValid = phone.trim().replace(/\D/g, '').length >= 10;
    if (step === 1) return name.trim() !== '' && phoneIsValid;
    if (step === 3) return isRetaker !== null && isSupplement !== null;
    if (step === 5) return archetype !== null || isCustomizing;
    if (step === 6) return studyDays.length > 0;
    return true;
  };

  return (
    <div className="setup-container theme-aware">
      <div className="setup-panel card-container">
        <div className="setup-progress-bar">
            <div className="progress-fill" style={{ width: `${(step / 7) * 100}%` }}></div>
        </div>
        
        <div className="setup-step-content">
            {getStepContent()}
        </div>
        
        {step < 7 && (
            <div className="setup-nav">
                <button 
                    className="btn secondary" 
                    onClick={() => setStep(prev => prev - 1)} 
                    disabled={step === 1}
                    style={{visibility: step === 1 ? 'hidden' : 'visible'}}
                >
                    Back
                </button>
                <button 
                    className="btn primary" 
                    onClick={() => setStep(prev => prev + 1)}
                    disabled={!canProceed()}
                >
                    Next
                </button>
            </div>
        )}
      </div>
    </div>
  );
}

export default SetupView;